import { ComponentProps, useCallback, useMemo, useState } from 'react';
import CheckboxGroup from './CheckboxGroup';

type CheckboxGroupProps = ComponentProps<typeof CheckboxGroup>;

const useCheckboxGroupOptions = (initialOptions: CheckboxGroupProps['options'], selectAllOption?: boolean, defaultIds: string[] = []) => {
  const [options, setOptions] = useState(initialOptions);

  const onChange: CheckboxGroupProps['onChange'] = useCallback(
    (id, selected) => {
      const changedIndex = options.findIndex(option => option.id === id);
      if (selectAllOption) {
        if (changedIndex === 0) {
          // Select/unselect all options to match the new "Select all" state
          const newOptions = options.map(option => ({ ...option, selected }));
          setOptions(newOptions);
        } else {
          const newOptions = [...options];
          newOptions[changedIndex] = { ...options[changedIndex], selected };

          // Check to see if all options now have the same selection state in order to update the "Select all" option
          const allTheSame = newOptions.slice(1).findIndex(option => option.selected !== selected) === -1;
          if (allTheSame) {
            newOptions[0] = { ...options[0], selected };
          } else {
            newOptions[0] = { ...options[0], selected: 'indeterminate' };
          }

          setOptions(newOptions);
        }
      } else {
        const newOptions = [...options];
        newOptions[changedIndex] = { ...options[changedIndex], selected };
        setOptions(newOptions);
      }
    },
    [options, selectAllOption]
  );

  const selectedOptions = useMemo(() => {
    return (selectAllOption ? options.slice(1) : options).filter(option => option.selected === true);
  }, [options, selectAllOption]);

  const clearOptions = useMemo(() => {
    if (selectedOptions.length === defaultIds.length && selectedOptions.every(option => defaultIds.includes(option.id))) {
      return undefined;
    }
    return () => {
      const clearedOptions = options.map(option => ({ ...option, selected: defaultIds.includes(option.id) }));
      setOptions(clearedOptions);
    };
  }, [defaultIds, options, selectedOptions]);

  return useMemo(() => ({ clearOptions, onChange, options, selectedOptions, setOptions }), [clearOptions, onChange, options, selectedOptions]);
};

export default useCheckboxGroupOptions;
